import { useEffect, useRef, useState } from "react";
import { errorHandler } from "../../../utils";
import { convertToBase64, determineFileType } from "../../../utils/functions";
import { closeJob, completeJob } from "../../../services/clientCalls";
import {
  IconCircleCheck,
  IconTrophy,
  IconUserCheck,
  IconCalendarEvent,
  IconPaperclip,
  IconCloudUpload,
  IconFileCheck,
  IconX,
  IconNotes,
  IconInfoCircle,
  IconLockOpen,
  IconArrowLeft,
  IconBriefcaseOff,
  IconAlertTriangle,
  IconListDetails,
} from "@tabler/icons-react";
import m from "../../Modal/modal.module.css";
import s from "./modal.module.css";
import Button from "../../UI/Button/button";
import Input, { Select, Textarea } from "../../UI/FormInput/input";

export const MarkJobModal = ({
  setShowModal,
  setCompleteModal,
  setCancelModal,
  markType,
  title,
  type,
  applicantID,
  job,
  open,
  onClose,
}) => {
  const [load, setLoad] = useState(false);
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [forms, setForms] = useState({
    clientAdditionalReason: "",
    clientSignature: "",
    clientSignatureDate: new Date(),
    clientFiles: [],
  });

  useEffect(() => {
    setForms({
      ...forms,
      clientFiles: files,
    });
  }, [files]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (load) return;
    if (!applicantID) {
      errorHandler("This job cannot be updated because it is missing an id.", "Error");
      return;
    }
    setLoad(true);
    try {
      const payload = {
        ...forms,
        clientAdditionalReason: forms.clientAdditionalReason || "Confirmed by client",
        clientSignature: forms.clientSignature || "N/A",
        clientSignatureDate: forms.clientSignatureDate || new Date().toISOString().split("T")[0],
        clientFiles: Array.isArray(forms.clientFiles) ? forms.clientFiles : [],
      };
      if (markType === "complete") {
        const res = await completeJob(applicantID, payload);
        if (res.data?.success) {
          setShowModal(false);
          setCompleteModal(true);
        }
      } else {
        const res = await closeJob(applicantID, payload);
        if (res.data?.success) {
          setShowModal(false);
          setCancelModal(true);
        }
      }
    } catch (error) {
      error.response?.data
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  };
  const handleFileUpload = async (event) => {
    const uploadedFiles = event.target.files;

    const filesArray = Array.from(uploadedFiles).map(async (file) => {
      const base64File = await convertToBase64(file);
      const type = determineFileType(file);
      return {
        filename: file.name,
        base64File: base64File,
        type: type, // Automatically determine the type
      };
    });

    // Resolve all promises to get an array of file objects
    const resolvedFiles = await Promise.all(filesArray);
    setFiles((prev) => [...prev, ...resolvedFiles]);
  };

  const handleTypeChange = (index, type) => {
    const updatedFiles = [...files];
    updatedFiles[index].type = type; // Update the type for the selected file
    setFiles(updatedFiles);
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  if (!open || !job) return null;

  const reset = () => {
    setFiles([]);
    setForms({
      ...forms,
      clientAdditionalReason: "",
      clientSignature: "",
      clientSignatureDate: new Date(),
      clientFiles: [],
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div
      className={m.backdrop}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className={`${m.card} ${m.cardSm}`}>
        <div className={m.head}>
          <div
            className={m.headIcon}
            style={{ background: "#ecfdf5", color: "#059669" }}
          >
            <IconCircleCheck size={22} />
          </div>
          <div className={m.headInfo}>
            <div className={m.headTitle}>Mark as Completed</div>
            <div className={m.headSub}>{job?.title}</div>
          </div>
          <button
            className={m.closeBtn}
            onClick={handleClose}
            aria-label="Close"
          >
            <IconX size={17} />
          </button>
        </div>

        <div className={m.body}>
          <div className={`${s.banner} ${s.bannerComplete}`}>
            <div className={s.bannerIcon}>
              <IconTrophy size={24} />
            </div>
            <div>
              <div className={s.bannerTitle}>Congratulations on the hire!</div>
              <div className={s.bannerSub}>
                Fill in the details below or skip straight to confirming.
              </div>
            </div>
          </div>

          <div className={s.field}>
            <Input
              label={
                <>
                  Hired Candidate
                  <span className={s.optionalBadge}>
                    <IconLockOpen size={11} /> Optional
                  </span>
                </>
              }
              id="cj-candidate"
              leftIcon={<IconUserCheck size={16} />}
              placeholder="e.g. Ada Okonkwo"
              value={forms?.clientSignature}
              onChange={(e) => {
                setForms((prevState) => ({
                  ...prevState,
                  clientSignature: e.target.value,
                }));
              }}
            />
          </div>

          <div className={s.field}>
            <Input
              label={
                <>
                  Start Date
                  <span className={s.optionalBadge}>
                    <IconLockOpen size={11} /> Optional
                  </span>
                </>
              }
              id="cj-date"
              type="date"
              leftIcon={<IconCalendarEvent size={16} />}
              value={forms?.clientSignatureDate}
              onChange={(e) =>
                setForms((prev) => ({
                  ...prev,
                  clientSignatureDate: e.target.value,
                }))
              }
            />
          </div>

          <div className={s.field}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--navy)",
                marginBottom: 7,
                gap: 4,
              }}
            >
              <IconPaperclip size={14} /> Supporting Evidence
              <span className={s.optionalBadge}>
                <IconLockOpen size={11} /> Optional
              </span>
            </label>
            <div
              className={`${s.uploadZone} ${dragOver ? s.uploadZoneDrag : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <div className={s.uploadIcon}>
                <IconCloudUpload size={22} />
              </div>
              <div className={s.uploadTitle}>Drop your file here</div>
              <div className={s.uploadSub}>
                or <span>browse to upload</span>
              </div>
              <div className={s.fileTypes}>
                <span className={s.fileType}>PDF</span>
                <span className={s.fileType}>DOC</span>
                <span className={s.fileType}>JPG</span>
                <span className={s.fileType}>PNG</span>
              </div>
            </div>
            {file && (
              <div className={s.filePreview}>
                <IconFileCheck size={18} />
                <span className={s.fileName}>{file.name}</span>
                <button
                  className={s.fileRemove}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  title="Remove file"
                >
                  <IconX size={16} />
                </button>
              </div>
            )}
          </div>

          <div className={s.field}>
            <Textarea
              label={
                <>
                  Completion Notes
                  <span className={s.optionalBadge}>
                    <IconLockOpen size={11} /> Optional
                  </span>
                </>
              }
              id="cj-notes"
              leftIcon={<IconNotes size={16} />}
              placeholder="Add any notes about this hire — how it went, feedback on the process, etc."
              value={forms?.clientAdditionalReasonSummary}
              onChange={(e) => {
                setForms((prevState) => ({
                  ...prevState,
                  clientAdditionalReasonSummary: e.target.value,
                }));
              }}
            />
          </div>

          <div className={s.skipHint}>
            <IconInfoCircle size={14} />
            All fields are optional. You can click&nbsp;
            <strong>Mark as Completed</strong>&nbsp;now to skip.
          </div>
        </div>

        <div className={m.foot}>
          <Button
            variant="ghost"
            leftIcon={<IconArrowLeft size={15} />}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            loading={load}
            disabled={load}
            leftIcon={<IconCircleCheck size={15} />}
            onClick={handleSubmit}
          >
            Mark as Completed
          </Button>
        </div>
      </div>
    </div>
  );
};

const REASONS = [
  { value: "", label: "Select a reason…" },
  { value: "filled", label: "Role filled internally" },
  { value: "external", label: "Hired through another channel" },
  { value: "budget", label: "Budget cut or freeze" },
  { value: "paused", label: "Hiring paused" },
  { value: "restructure", label: "Role restructured or removed" },
  { value: "other", label: "Other" },
];

/* Close Job Modal */
export const CloseJobModal = ({
  job,
  open,
  onClose,
  onConfirm,
  setShowModal,
  setCompleteModal,
  setCancelModal,
  markType,
  applicantID,
}) => {
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [load, setLoad] = useState(false);

  const [forms, setForms] = useState({
    clientAdditionalReason: "",
    clientSignature: "",
    clientSignatureDate: new Date(),
    clientFiles: [],
  });

  useEffect(() => {
    setForms({
      ...forms,
      clientFiles: file,
    });
  }, [file]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (load) return;
    if (!applicantID) {
      errorHandler("This job cannot be closed because it is missing an id.", "Error");
      return;
    }
    setLoad(true);
    try {
      let clientFiles = [];
      if (file) {
        try {
          const base64File = await convertToBase64(file);
          const type = determineFileType(file);
          clientFiles = [
            {
              filename: file.name,
              base64File,
              type,
            },
          ];
        } catch (fileError) {
          errorHandler("Could not attach that file. Closing without a document.", "Error");
          clientFiles = [];
        }
      }
      const reasonLabel = REASONS.find((r) => r.value === reason)?.label || "";
      const notes = String(forms?.clientAdditionalReason || "").trim();
      const payload = {
        clientAdditionalReason:
          [reasonLabel, notes].filter(Boolean).join(" — ") || "Closed by client",
        clientSignature: "listing-close",
        clientSignatureDate: new Date().toISOString().split("T")[0],
        clientFiles,
      };
      const res = await closeJob(applicantID, payload);
      if (res.data?.success) {
        setShowModal(false);
        setCancelModal(true);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  };
  // const handleFileUpload = async (event) => {
  //   const uploadedFiles = event.target.files;

  //   const filesArray = Array.from(uploadedFiles).map(async (file) => {
  //     const base64File = await convertToBase64(file);
  //     const type = determineFileType(file);
  //     return {
  //       filename: file.name,
  //       base64File: base64File,
  //       type: type, // Automatically determine the type
  //     };
  //   });

  //   // Resolve all promises to get an array of file objects
  //   const resolvedFiles = await Promise.all(filesArray);
  //   setFiles((prev) => [...prev, ...resolvedFiles]);
  // };

  // const handleTypeChange = (index, type) => {
  //   const updatedFiles = [...files];
  //   updatedFiles[index].type = type; // Update the type for the selected file
  //   setFiles(updatedFiles);
  // };

  // const handleRemoveFile = (index) => {
  //   setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  // };

  if (!open || !job) return null;

  const reset = () => {
    setForms({
      clientAdditionalReason: "",
      clientSignature: "",
      clientSignatureDate: new Date(),
      clientFiles: [],
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div
      className={m.backdrop}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className={`${m.card} ${m.cardSm}`}>
        <div className={m.head}>
          <div
            className={m.headIcon}
            style={{ background: "#fef2f2", color: "#dc2626" }}
          >
            <IconBriefcaseOff size={22} />
          </div>
          <div className={m.headInfo}>
            <div className={m.headTitle}>Close Job Listing</div>
            <div className={m.headSub}>{job.title}</div>
          </div>
          <button
            className={m.closeBtn}
            onClick={handleClose}
            aria-label="Close"
          >
            <IconX size={17} />
          </button>
        </div>

        <div className={m.body}>
          <div className={`${s.banner} ${s.bannerClose}`}>
            <div className={s.bannerIcon}>
              <IconAlertTriangle size={24} />
            </div>
            <div>
              <div className={s.bannerTitle}>
                This listing will stop receiving applications
              </div>
              <div className={s.bannerSub}>
                Tell us why, or skip straight to closing.
              </div>
            </div>
          </div>

          <div className={s.field}>
            <Select
              label={
                <>
                  Reason for Closing
                  <span className={s.optionalBadge}>
                    <IconLockOpen size={11} /> Optional
                  </span>
                </>
              }
              id="clj-reason"
              leftIcon={<IconListDetails size={16} />}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value} disabled={r.value === ""}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>

          <div className={s.field}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--navy)",
                marginBottom: 7,
                gap: 4,
              }}
            >
              <IconPaperclip size={14} /> Supporting Document
              <span className={s.optionalBadge}>
                <IconLockOpen size={11} /> Optional
              </span>
            </label>
            <div
              className={`${s.uploadZone} ${s.uploadZoneClose} ${dragOver ? s.uploadZoneCloseDrag : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <div className={`${s.uploadIcon} ${s.uploadIconClose}`}>
                <IconCloudUpload size={22} />
              </div>
              <div className={s.uploadTitle}>Drop your file here</div>
              <div className={s.uploadSub}>
                or <span>browse to upload</span>
              </div>
              <div className={s.fileTypes}>
                <span className={s.fileType}>PDF</span>
                <span className={s.fileType}>DOC</span>
                <span className={s.fileType}>JPG</span>
                <span className={s.fileType}>PNG</span>
              </div>
            </div>
            {file && (
              <div className={`${s.filePreview} ${s.filePreviewClose}`}>
                <IconFileCheck size={18} />
                <span className={s.fileName}>{file.name}</span>
                <button
                  className={s.fileRemove}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  title="Remove file"
                >
                  <IconX size={16} />
                </button>
              </div>
            )}
          </div>

          <div className={s.field}>
            <Textarea
              label={
                <>
                  Additional Notes
                  <span className={s.optionalBadge}>
                    <IconLockOpen size={11} /> Optional
                  </span>
                </>
              }
              id="clj-notes"
              placeholder="Any context about this closure — internal feedback, timeline, next steps…"
              value={forms?.clientAdditionalReason}
              onChange={(e) => {
                setForms((prevState) => ({
                  ...prevState,
                  clientAdditionalReason: e.target.value,
                }));
              }}
            />
          </div>

          <div className={s.skipHint}>
            <IconInfoCircle size={14} />
            All fields are optional. Click&nbsp;<strong>Close Listing</strong>
            &nbsp;to skip and close immediately.
          </div>
        </div>

        <div className={m.foot}>
          <Button
            variant="ghost"
            leftIcon={<IconArrowLeft size={15} />}
            onClick={handleClose}
          >
            Keep Active
          </Button>
          <Button
            variant="danger"
            loading={load}
            disabled={load}
            leftIcon={<IconBriefcaseOff size={15} />}
            onClick={handleSubmit}
          >
            Close Listing
          </Button>
        </div>
      </div>
    </div>
  );
};
